import {
  AccountBalance,
  AccountBalanceOutlined,
  Apartment,
  ArrowBack,
  AttachMoney,
  BadgeOutlined,
  Business,
  BusinessCenter,
  CheckCircle,
  Description,
  FlashOn,
  GroupOutlined,
  History,
  HowToReg,
  Link,
  LocalAtm,
  LocalOffer,
  LockOpenOutlined,
  LockOutlined,
  Mail,
  MonetizationOn,
  Payment,
  Payments,
  Person2Outlined,
  PersonAddAlt,
  PriceCheck,
  ReceiptOutlined,
  RequestPageOutlined,
  Settings,
  ShowChart,
  StickyNote2,
  SupervisedUserCircle,
  SyncAlt,
  TableChart,
  ThumbUp,
  TrendingUp,
  ViewQuilt,
  Workspaces,
} from '@mui/icons-material';
import { CalendarIcon } from '@mui/x-date-pickers';
import {
  ADD_CUSTOM_UPDATE,
  ADMIN_USER,
  ADMIN_USER_ROLES,
  ADVISER,
  ADVISER_ADD,
  ALLOCATION_HISTORY,
  ALL_FUNDS,
  BANK_RECONCILIATION,
  BILLING,
  BULK_PAYMENT_ASSIGNMENT,
  CASH_TRANSACTIONS_REPORT,
  CHANGE_LOG,
  COMMITMENTS,
  COMPANY,
  CORE_FEE_TEMPLATES,
  CREATE_ALLOCATION,
  DASHBOARD_ALL_FIRMS,
  DASHBOARD_MY_FIRM,
  DATA_TOOLS,
  EIS,
  EIS_ADD,
  EQUALISATIONS,
  EQUALISATIONS_ADD,
  EXITS,
  EXITS_ADD,
  FEE_MANAGER,
  FEE_SUMMARY,
  FIRM,
  FUND_SETUP,
  FURTHER_USER,
  GENERAL_LEDGER,
  INCOME_OR_EXPENSE_LIST,
  INTEREST_PAYMENTS,
  INVESTMENT,
  INVESTMENT_ADD,
  INVESTMENT_HOLDINGS_REPORT,
  INVESTMENT_TRANSACTIONS_REPORT,
  INVESTMENT_TRANSFERS,
  INVESTOR,
  INVESTOR_BULLETIN,
  INVESTOR_REPORTING,
  INVESTOR_REPORTING_ADD,
  INVESTOR_UPLOAD,
  KI_EIS_ADD,
  LINKED_PORTALS,
  LP_CAPITAL_ACTION_CREATE,
  LP_CAPITAL_ACTION_LIST,
  LP_COMPANY_ADD,
  LP_COMPANY_LIST,
  LP_FIRM_SETTINGS,
  LP_INVESTMENT_CREATE,
  LP_INVESTMENT_LIST,
  LP_MANUAL_ACTION_CREATE,
  LP_MANUAL_ACTION_LIST,
  LP_ONBOARDING,
  LP_PERIOD_END_REVIEW,
  LP_REALISATION_CREATE,
  LP_REALISATION_LIST,
  ONE_OFF_FEES_AND_DISCOUNTS,
  ONGOING_AML_REVIEW,
  ORGANISATION,
  ORGANISATION_ADD,
  PARTNER_TRANSFERS_CREATE,
  PARTNER_TRANSFERS_LIST,
  PERFORMANCE_FEE_TEMPLATES,
  PORTAL_USAGE_DATA_REPORT,
  PROFILE,
  SUBSCRIPTION_FUNNEL,
  TAG,
  TAX_REPORT_AUDIT,
  TRANCHE,
  USER_MANAGEMENT,
  VEHICLES,
  WITHDRAWALS,
  WITHDRAWALS_ADD,
  createIncomeOrExpenseAddRoute,
} from 'adminConstants/routes';
import { RolePermission } from 'further-types/admin-user-role';

export type MenuItemType = {
  name: string;
  type: string;
  link?: string;
  icon?: React.ReactNode;
  onlyShownForRoles?: Array<string>;
  hiddenForRoles?: Array<string>;
  children?: Array<MenuItemType>;
  permission?: string;
  /** Show when user has any of these permissions (overrides permission when set). */
  anyOfPermissions?: Array<string>;
  isLogout?: boolean;
  onClick?: () => void;
};

export const itemsContainUrl = (children: Array<MenuItemType>, url: string) => {
  if (!children?.length) {
    return false;
  }

  for (const child of children) {
    if (child.children) {
      if (itemsContainUrl(child.children, url)) {
        return true;
      }
    }

    if (child.link === url || (child.link && url.includes(child.link))) {
      return true;
    }
  }
  return false;
};

const isItemVisibleForUser = (
  item: MenuItemType,
  permissionsList: Array<string> | undefined,
  roleSlug: string | undefined,
) => {
  const isPermittedForRole =
    !item.onlyShownForRoles?.length ||
    item.onlyShownForRoles.includes(roleSlug ?? '');
  const isNotHiddenForRole =
    !item.hiddenForRoles?.length ||
    !item.hiddenForRoles.includes(roleSlug ?? '');
  const permissionOk = item.anyOfPermissions?.length
    ? item.anyOfPermissions.some((p) => permissionsList?.includes(p))
    : !item.permission || Boolean(permissionsList?.includes(item.permission));
  return isPermittedForRole && isNotHiddenForRole && permissionOk;
};

export const filterVisibleMenuItems = (
  items: Array<MenuItemType>,
  permissionsList: Array<string> | undefined,
  roleSlug: string | undefined,
): Array<MenuItemType> => // needs return type annotation here as it is recursive
  items.reduce<Array<MenuItemType>>((acc, item) => {
    if (!isItemVisibleForUser(item, permissionsList, roleSlug)) {
      return acc;
    }
    if (!item.children?.length) {
      return [...acc, { ...item }];
    }
    const filteredChildren = filterVisibleMenuItems(
      item.children,
      permissionsList,
      roleSlug,
    );
    return filteredChildren.length
      ? [...acc, { ...item, children: filteredChildren }]
      : acc;
  }, []);

export const createLpSidebarNav = (): Array<MenuItemType> => [
  {
    name: 'Fund Administration',
    type: 'section',
    children: [
      {
        name: 'Fund Setup',
        icon: <PriceCheck />,
        link: FUND_SETUP,
        type: 'item',
        permission: RolePermission.LpReadVehicles,
      },
      {
        name: 'Onboarding',
        icon: <PersonAddAlt />,
        link: LP_ONBOARDING,
        type: 'item',
        permission: RolePermission.LpReadCommitment,
      },

      {
        name: 'Commitments',
        icon: <CheckCircle />,
        link: COMMITMENTS,
        type: 'item',
        permission: RolePermission.LpReadCommitment,
      },
      {
        name: 'Close & Equalise',
        icon: <SyncAlt />,
        link: EQUALISATIONS,
        type: 'collapse',
        permission: RolePermission.LpReadEqualisations,
        children: [
          {
            name: 'View Closes',
            link: EQUALISATIONS,
            type: 'item',
            permission: RolePermission.LpReadEqualisations,
          },
          {
            name: 'Add Close',
            link: EQUALISATIONS_ADD,
            type: 'item',
            permission: RolePermission.LpCreateEqualisation,
          },
        ],
      },
      {
        name: 'Capital Actions',
        icon: <LocalAtm />,
        type: 'collapse',
        permission: RolePermission.LpReadCapitalAction,
        children: [
          {
            name: 'View Capital Actions',
            link: LP_CAPITAL_ACTION_LIST,
            type: 'item',
            permission: RolePermission.LpReadCapitalAction,
          },
          {
            name: 'Add Capital Action',
            link: LP_CAPITAL_ACTION_CREATE,
            type: 'item',
            permission: RolePermission.LpCreateCapitalAction,
          },
        ],
      },
      {
        name: 'Fund Accounting',
        icon: <MonetizationOn />,
        type: 'collapse',
        permission: RolePermission.ReadGl,
        children: [
          {
            name: 'General Ledger',
            link: GENERAL_LEDGER,
            type: 'item',
            permission: RolePermission.ReadGl,
          },
        ],
      },
      {
        name: 'Bank Reconciliation',
        icon: <AccountBalanceOutlined />,
        type: 'item',
        link: BANK_RECONCILIATION,
        permission: RolePermission.LpReadBankAccount,
      },
      {
        name: 'Partner Transfers',
        icon: <Person2Outlined />,
        type: 'collapse',
        permission: RolePermission.LpReadPartnerTransfers,
        children: [
          {
            name: 'View Partner Transfers',
            link: PARTNER_TRANSFERS_LIST,
            type: 'item',
            permission: RolePermission.LpReadPartnerTransfers,
          },
          {
            name: 'Create Partner Transfer',
            link: PARTNER_TRANSFERS_CREATE,
            type: 'item',
            permission: RolePermission.LpCreatePartnerTransfer,
          },
        ],
      },
      {
        name: 'Fee Manager',
        icon: <RequestPageOutlined />,
        type: 'item',
        link: FEE_MANAGER,
        permission: RolePermission.LpReadFee,
      },
      {
        name: 'Income & Expenses',
        icon: <SyncAlt />,
        type: 'collapse',
        permission: RolePermission.LpReadIncomeAndExpenses,
        children: [
          {
            name: 'View Income & Expenses',
            link: INCOME_OR_EXPENSE_LIST,
            type: 'item',
            permission: RolePermission.LpReadIncomeAndExpenses,
          },
          {
            name: 'Add Income or Expense',
            link: createIncomeOrExpenseAddRoute(),
            type: 'item',
            permission: RolePermission.LpCreateIncomeAndExpenses,
          },
        ],
      },
      {
        name: 'Investments',
        type: 'collapse',
        icon: <TrendingUp />,
        permission: RolePermission.LpReadInvestment,
        children: [
          {
            name: 'View Investments',
            link: LP_INVESTMENT_LIST,
            type: 'item',
            permission: RolePermission.LpReadInvestment,
          },
          {
            name: 'Add Investment',
            link: LP_INVESTMENT_CREATE,
            type: 'item',
            permission: RolePermission.LpCreateInvestment,
          },
        ],
      },
      {
        name: 'Realisations',
        icon: <Payments />,
        type: 'collapse',
        permission: RolePermission.LpReadRealisation,
        children: [
          {
            name: 'View Realisations',
            link: LP_REALISATION_LIST,
            type: 'item',
            permission: RolePermission.LpReadRealisation,
          },
          {
            name: 'Add Realisation',
            link: LP_REALISATION_CREATE,
            type: 'item',
            permission: RolePermission.LpCreateRealisation,
          },
        ],
      },
      {
        name: 'Holdings',
        icon: <Apartment />,
        type: 'collapse',
        permission: RolePermission.LpReadAssets,
        children: [
          {
            name: 'View Holdings',
            link: LP_COMPANY_LIST,
            type: 'item',
            permission: RolePermission.LpReadAssets,
          },
          {
            name: 'Add Holding',
            link: LP_COMPANY_ADD,
            type: 'item',
            permission: RolePermission.LpCreateAsset,
          },
        ],
      },
      {
        name: 'Period End Review',
        icon: <CalendarIcon />,
        link: LP_PERIOD_END_REVIEW,
        type: 'item',
        permission: RolePermission.LpReadAccountingPeriod,
      },
      {
        name: 'Manual Journal',
        icon: <StickyNote2 />,
        type: 'collapse',
        permission: RolePermission.LpReadManualJournal,
        children: [
          {
            name: 'View Manual Journals',
            link: LP_MANUAL_ACTION_LIST,
            type: 'item',
            permission: RolePermission.LpReadManualJournal,
          },
          {
            name: 'Add Manual Journal',
            link: LP_MANUAL_ACTION_CREATE,
            type: 'item',
            permission: RolePermission.LpCreateManualAction,
          },
        ],
      },
    ],
  },
  {
    type: 'section',
    name: 'Settings',
    children: [
      {
        name: 'Vehicles',
        icon: <Workspaces />,
        link: VEHICLES,
        type: 'item',
        permission: RolePermission.LpReadVehicles,
      },
      {
        name: 'User Management',
        icon: <SupervisedUserCircle />,
        link: USER_MANAGEMENT,
        type: 'item',
        permission: RolePermission.ReadUsers,
      },
      {
        name: 'Firm Settings',
        icon: <Settings />,
        link: LP_FIRM_SETTINGS,
        type: 'item',
        permission: RolePermission.EditFirm,
      },
      {
        name: 'Logout',
        icon: <ArrowBack />,
        type: 'item',
        isLogout: true,
      },
    ],
  },
];

export const createEisSidebarNav = (
  myFirmLink: string,
  openTrancheDashboardLinks?: Array<MenuItemType>,
  firmDashboardLinks?: Array<MenuItemType>,
): Array<MenuItemType> => [
  {
    name: 'Summary',
    type: 'section',
    children: [
      {
        name: 'Admin Dashboard',
        icon: <ViewQuilt />,
        link: DASHBOARD_ALL_FIRMS,
        type: 'item',
        onlyShownForRoles: ['super-admin'],
      },
      firmDashboardLinks?.length
        ? {
            name: 'Firm Dashboard',
            type: 'collapse',
            icon: <ViewQuilt />,
            permission: RolePermission.ReadDashboards,
            children: firmDashboardLinks,
          }
        : {
            name: 'Firm Dashboard',
            type: 'item',
            icon: <ViewQuilt />,
            link: DASHBOARD_MY_FIRM,
            permission: RolePermission.ReadDashboards,
          },
      ...(openTrancheDashboardLinks?.length
        ? [
            {
              name: 'Open Tranches',
              type: 'collapse',
              icon: <LockOpenOutlined />,
              permission: RolePermission.ReadDashboards,
              children: openTrancheDashboardLinks,
            },
          ]
        : []),
      {
        name: 'All Tranches',
        type: 'item',
        icon: <LockOutlined />,
        link: ALL_FUNDS,
        permission: RolePermission.ReadTranche,
      },
    ],
  },
  {
    name: 'Fund Administration',
    type: 'section',
    children: [
      {
        name: 'My Firm',
        type: 'item',
        icon: <Business />,
        link: myFirmLink,
        permission: RolePermission.ReadFirm,
        hiddenForRoles: ['super-admin'],
      },
      {
        name: 'Firms',
        type: 'item',
        icon: <Business />,
        link: FIRM,
        permission: RolePermission.ReadFirm,
        onlyShownForRoles: ['super-admin'],
      },
      {
        name: 'Tranches',
        type: 'item',
        icon: <Description />,
        link: TRANCHE,
        permission: RolePermission.ReadTranche,
      },
      {
        name: 'Users',
        type: 'item',
        icon: <SupervisedUserCircle />,
        link: FURTHER_USER,
        permission: RolePermission.ReadFurtherUser,
      },
      {
        name: 'Investors',
        type: 'collapse',
        icon: <HowToReg />,
        permission: RolePermission.ReadInvestor,
        children: [
          {
            name: 'View Investors',
            type: 'item',
            link: INVESTOR,
          },
          {
            name: 'Upload Investors',
            type: 'item',
            link: INVESTOR_UPLOAD,
            anyOfPermissions: [
              RolePermission.CreateInvestor,
              RolePermission.UploadInvestorFull,
            ],
          },
          {
            name: 'Ongoing AML Review',
            type: 'item',
            link: ONGOING_AML_REVIEW,
          },
        ],
      },

      {
        name: 'Subscriptions',
        type: 'collapse',
        icon: <ShowChart />,
        anyOfPermissions: [
          RolePermission.ReadInvestment,
          RolePermission.CreateInvestment,
          RolePermission.UploadInvestorBasic,
          RolePermission.UploadInvestorFull,
        ],
        children: [
          {
            name: 'View Subscriptions',
            type: 'item',
            link: INVESTMENT,
            permission: RolePermission.ReadInvestment,
          },
          {
            name: 'Upload Subscriptions',
            type: 'item',
            link: INVESTOR_UPLOAD,
            anyOfPermissions: [
              RolePermission.CreateInvestment,
              RolePermission.UploadInvestorBasic,
              RolePermission.UploadInvestorFull,
            ],
          },
          {
            name: 'Create Subscription',
            type: 'item',
            link: INVESTMENT_ADD,
            permission: RolePermission.CreateInvestment,
          },
          {
            name: 'Interest Payments',
            type: 'item',
            link: INTEREST_PAYMENTS,
            permission: RolePermission.ReadInterest,
          },
          {
            name: 'Subscription Funnel',
            type: 'item',
            link: SUBSCRIPTION_FUNNEL,
            permission: RolePermission.ReadInvestment,
          },
          {
            name: 'Transfer Record',
            type: 'item',
            link: INVESTMENT_TRANSFERS,
            permission: RolePermission.ReadInvestment,
          },
        ],
      },
      {
        name: 'Payments',
        type: 'collapse',
        icon: <Payment />,
        permission: RolePermission.ReadPaymentExpectations,
        children: [
          {
            name: 'Bulk Payment Assignment',
            type: 'item',
            link: BULK_PAYMENT_ASSIGNMENT,
          },
        ],
      },
      {
        name: 'Fees',
        type: 'collapse',
        icon: <MonetizationOn />,
        permission: RolePermission.ReadInvestment,
        children: [
          {
            name: 'Fees Summary',
            type: 'item',
            link: FEE_SUMMARY,
          },
          {
            name: 'One-Off Fees / Discounts',
            type: 'item',
            link: ONE_OFF_FEES_AND_DISCOUNTS,
          },
          {
            name: 'Core Fee Templates',
            type: 'item',
            link: CORE_FEE_TEMPLATES,
          },
          {
            name: 'Performance Fee Templates',
            type: 'item',
            link: PERFORMANCE_FEE_TEMPLATES,
          },
        ],
      },
      {
        name: 'Withdrawals',
        type: 'collapse',
        icon: <AccountBalance />,
        permission: RolePermission.ReadWithdrawals,
        children: [
          {
            name: 'Request Withdrawals',
            type: 'item',
            link: WITHDRAWALS_ADD,
            permission: RolePermission.RequestWithdrawals,
          },
          {
            name: 'Review Withdrawals',
            type: 'item',
            link: WITHDRAWALS,
          },
        ],
      },
      {
        name: 'Advisers',
        type: 'collapse',
        icon: <GroupOutlined />,
        anyOfPermissions: [
          RolePermission.ReadAdvisers,
          RolePermission.CreateAdvisers,
          RolePermission.ReadOrganisations,
          RolePermission.CreateOrganisations,
        ],
        children: [
          {
            name: 'View Advisers',
            type: 'item',
            link: ADVISER,
            permission: RolePermission.ReadAdvisers,
          },
          {
            name: 'Create Adviser',
            type: 'item',
            link: ADVISER_ADD,
            permission: RolePermission.CreateAdvisers,
          },
          {
            name: 'View Organisations',
            type: 'item',
            link: ORGANISATION,
            permission: RolePermission.ReadOrganisations,
          },
          {
            name: 'Create Adviser Organisation',
            type: 'item',
            link: ORGANISATION_ADD,
            permission: RolePermission.CreateOrganisations,
          },
        ],
      },
      {
        name: 'System Exports',
        type: 'collapse',
        icon: <ReceiptOutlined />,
        permission: RolePermission.CreateBackgroundExport,
        children: [
          {
            name: 'Cash Transactions',
            type: 'item',
            link: CASH_TRANSACTIONS_REPORT,
          },
          {
            name: 'Investment Holdings',
            type: 'item',
            link: INVESTMENT_HOLDINGS_REPORT,
          },
          {
            name: 'Subscription Transactions',
            type: 'item',
            link: INVESTMENT_TRANSACTIONS_REPORT,
          },
          {
            name: 'Portal Usage Data',
            type: 'item',
            link: PORTAL_USAGE_DATA_REPORT,
          },
        ],
      },
    ],
  },
  {
    name: 'Portfolio',
    type: 'section',
    children: [
      {
        name: 'Companies',
        type: 'item',
        icon: <BusinessCenter />,
        link: COMPANY,
        permission: RolePermission.ReadCompanies,
      },
      {
        name: 'Allocations',
        type: 'collapse',
        icon: <TableChart />,
        permission: RolePermission.ViewAllocationUpload,
        children: [
          {
            name: 'Create New Allocation',
            type: 'item',
            link: CREATE_ALLOCATION,
            permission: RolePermission.CreateAllocationUpload,
          },
          {
            name: 'View Allocations',
            type: 'item',
            link: ALLOCATION_HISTORY,
          },
        ],
      },
      {
        name: 'S/EIS Certificates',
        type: 'collapse',
        icon: <FlashOn />,
        permission: RolePermission.ReadSeis,
        children: [
          {
            name: 'View S/EIS Tasklist',
            type: 'item',
            link: EIS,
          },
          {
            name: 'Create S/EIS Certificates',
            type: 'item',
            link: EIS_ADD,
            permission: RolePermission.CreateSeis,
          },
          {
            name: 'Create KI Fund Certificates',
            type: 'item',
            link: KI_EIS_ADD,
            permission: RolePermission.CreateSeis,
          },
        ],
      },
      {
        name: 'Exits',
        type: 'collapse',
        icon: <ThumbUp />,
        permission: RolePermission.ReadExit,
        children: [
          {
            name: 'View Exits',
            type: 'item',
            link: EXITS,
          },
          {
            name: 'Process New Exit',
            type: 'item',
            link: EXITS_ADD,
            permission: RolePermission.CreateExit,
          },
        ],
      },
      {
        name: 'Investor Communications',
        type: 'collapse',
        icon: <Mail />,
        permission: RolePermission.ReadInvestorCommunications,
        children: [
          {
            name: 'Investor Reporting',
            type: 'item',
            link: INVESTOR_REPORTING,
          },
          {
            name: 'Create Periodic Update',
            type: 'item',
            link: INVESTOR_REPORTING_ADD,
            permission: RolePermission.CreateReportPeriodic,
          },
          {
            name: 'Create Custom Update',
            type: 'item',
            link: ADD_CUSTOM_UPDATE,
            permission: RolePermission.CreateReportCustom,
          },
          {
            name: 'Investor Bulletin',
            type: 'item',
            link: INVESTOR_BULLETIN,
            permission: RolePermission.EditInvestorBulletin,
          },
          {
            name: 'Tax Report Audit',
            type: 'item',
            link: TAX_REPORT_AUDIT,
          },
        ],
      },
    ],
  },
  {
    type: 'section',
    name: 'Super Settings',
    onlyShownForRoles: ['super-admin'],
    children: [
      {
        name: 'Fund Types',
        type: 'item',
        icon: <LocalOffer />,
        link: TAG,
        onlyShownForRoles: ['super-admin'],
      },
      {
        name: 'Billing',
        type: 'item',
        icon: <AttachMoney />,
        link: BILLING,
        onlyShownForRoles: ['super-admin'],
      },
      {
        name: 'Change Log',
        type: 'item',
        icon: <History />,
        link: CHANGE_LOG,
        onlyShownForRoles: ['super-admin'],
      },
      {
        name: 'Linked Portals',
        type: 'item',
        icon: <Link />,
        link: LINKED_PORTALS,
        onlyShownForRoles: ['super-admin'],
      },
      {
        name: 'Roles',
        type: 'item',
        icon: <BadgeOutlined />,
        link: ADMIN_USER_ROLES,
        onlyShownForRoles: ['super-admin'],
      },
      {
        name: 'Data Tools',
        type: 'item',
        icon: <TableChart />,
        link: DATA_TOOLS,
        onlyShownForRoles: ['super-admin'],
      },
    ],
  },
  {
    type: 'section',
    name: 'Settings',
    children: [
      {
        name: 'User Management',
        type: 'item',
        icon: <SupervisedUserCircle />,
        link: ADMIN_USER,
        permission: RolePermission.ReadUsers,
      },
      {
        name: 'Profile',
        type: 'item',
        icon: <Settings />,
        link: PROFILE,
      },
      {
        name: 'Logout',
        type: 'item',
        isLogout: true,
        icon: <ArrowBack />,
      },
    ],
  },
];
